import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import getOrders from '@/shared/api/orders/getOrders';
import useDataGridTable from '@/shared/components/dataDisplay/DataGridTable/hooks/useDataGridTable';
import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import Order from '@/shared/types/order/order.type';

const tableColumnsList = [
  'billing_address',
  'billing_address2',
  'billing_city',
  'billing_country',
  'billing_name',
  'billing_phone',
  'billing_phone_ext',
  'billing_postcode',
  'billing_state',
  'currency',
  'delivery_address',
  'delivery_address2',
  'delivery_city',
  'delivery_country',
  'billing_first_name',
  'billing_last_name',
  'billing_email_address',
  'delivery_name',
  'delivery_phone',
  'delivery_phone_ext',
  'delivery_postcode',
  'delivery_state',
  'delivery_type',
  'delivery_first_name',
  'delivery_last_name',
  'delivery_email_address',
  'display_billing_address',
  'display_delivery_address',
  'email_address',
  'ext_order_id',
  'in_account_id',
  'in_campaign_id',
  'in_customer_id',
  'in_event_id',
  'in_invoice_id',
  'in_offer_id',
  'in_order_id',
  'in_tenant_id',
  'order_quote_id',
  'order_status',
  'order_total',
  'shipping_price',
  'shipping_profile_id',
  'shipping_profile_zone_id',
  'sub_total',
  'tax',
  'ts_created',
  'billing_same_as_delivery',
  'delivery_method',
  'invoice_number',
  'payment_method',
];

const tableRequiredColumns = [
  'ts_created',
  'in_order_id',
  'delivery_method',
  'order_status',
  'billing_name',
  'order_total',
  'billing_address',
  'billing_city',
  'billing_country',
  'actions',
];

const useOrdersTable_v2 = () => {
  const redirectLogin = useRedirectLogin();

  const { apiRef, model, requestBody } = useDataGridTable();
  const requestBodyRef = useRef<typeof requestBody | null>(null);

  const [table, setTable] = useState({
    data: [] as Order[],
    totalRows: 0,
    isLoading: false,
  });

  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList: tableColumnsList,
      tableRequiredColumns: tableRequiredColumns,
    });

  useEffect(() => {
    const fetchOrders = async () => {
      setTable((prev) => ({ ...prev, isLoading: true }));

      const response = await getOrders(requestBody, redirectLogin);

      setTable((prev) => ({ ...prev, isLoading: false }));

      if (response.success) {
        setTable((prev) => ({
          ...prev,
          data: response.data,
          totalRows: response.total_records || 0,
        }));
      }
    };

    if (!isEqual(requestBody, requestBodyRef.current)) {
      requestBodyRef.current = requestBody;

      void fetchOrders();
    }
  }, [requestBody, redirectLogin]);

  return {
    apiRef,
    model,
    table,
    columns,
    visibilityModel,
    setVisibilityModel,
  };
};

export default useOrdersTable_v2;
